import {
  Card,
  SecondaryDescriptionText,
  Steps,
  VerticalSpace,
} from '@brokerbay/design-components';
import { MailOutlined, UserOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router';
import { UserSuggestedUpdateStatus, UserSuggestedUpdateType } from '@bb/users';
import errorReporter from '@app/errorReporter';
import { UserSuggestedUpdateController } from 'api';
import { breakpoints, Modal, Text, Title, Button, message } from 'components';
import { AuthContext, UserSuggestedUpdateModalContext } from 'contexts';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ModalFooter } from '@app/react-common/Modal';
import type { IUserSuggestedUpdateAcceptUpdateResponse } from '@/app-package';

const { Step } = Steps;

const userUpdateModalValues: Record<string, todo> = {
  email: {
    title: `Would you like to update your email?`,
    label: 'Email',
    description: `We've detected a change to your email on the MLS. This
    email is different than the one you have on BrokerBay. Note:
    This will become your new login for BrokerBay if you choose to
    update and all notifications will be sent to that email going
    forward.`,
    updateValueText: (val: string) => val,
    handleKeepPrevErrMsg: (msg: string) =>
      `Failed to keep previous email: ${msg}`,
    handleUpdateErrMsg: (msg: string) => `Failed to update email: ${msg}`,
    handleUpdateSuccessMsg: `Email updated`,
    icon: (completed: boolean) => !completed && <MailOutlined />,
  },
  names: {
    title: `Would you like to update your name?`,
    label: 'Name',
    description: `We've detected a change to your name on the MLS. This
    name is different than the one you have on BrokerBay.`,
    updateValueText: (val: string[]) => (
      <VerticalSpace>
        <Text>
          <strong>First Name: </strong>
          {val[0]}
        </Text>
        <Text>
          <strong>Last Name: </strong>
          {val[1]}
        </Text>
      </VerticalSpace>
    ),
    handleKeepPrevErrMsg: (msg: string) =>
      `Failed to keep previous name: ${msg}`,
    handleUpdateErrMsg: (msg: string) => `Failed to update name: ${msg}`,
    handleUpdateSuccessMsg: `Name updated`,
    icon: (completed: boolean) => !completed && <UserOutlined />,
  },
};

export const UserSuggestedUpdateModal = () => {
  const history = useHistory();

  const { setAuth } = useContext(AuthContext);
  const { openModal, closeModal, visible, suggestedUpdates } = useContext(
    UserSuggestedUpdateModalContext,
  );

  const [currentUpdateStep, setCurrentUpdateStep] = useState(0);
  const [isWorking, setIsWorking] = useState(false);

  const handleNextUpdateStep = () => {
    if (currentUpdateStep + 1 === suggestedUpdates?.length) {
      closeModal();
    } else {
      setCurrentUpdateStep(currentUpdateStep + 1);
    }
  };

  const handleKeepPrevious = async (updateId: string, updateType: string) => {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await UserSuggestedUpdateController.dismissUpdate({
        suggestedUpdateId: updateId,
      });
    } catch (err) {
      let msg = err.message;
      if (err.isAxiosError && err.response?.data?.message) {
        msg = err.response.data.message;
      }
      errorReporter.error(err);
      message.error(userUpdateModalValues[updateType].errorMsg(msg));
    } finally {
      setIsWorking(false);
      handleNextUpdateStep();
    }
  };

  const handleSetAuth = (
    updateType: string,
    updateResult: IUserSuggestedUpdateAcceptUpdateResponse,
  ) => {
    setAuth(prev => ({
      ...prev,
      ...(updateType === UserSuggestedUpdateType.email && {
        email: updateResult.newValue as string,
      }),
      ...(updateType === UserSuggestedUpdateType.names && {
        firstName: updateResult.newValue[0],
        lastName: updateResult.newValue[1],
      }),
    }));

    if (history.location.pathname !== '/profile') {
      history.push('/profile');
    }
  };

  const handleUpdate = async (updateId: string, updateType: string) => {
    if (isWorking) return;
    setIsWorking(true);
    try {
      const result = await UserSuggestedUpdateController.acceptUpdate({
        suggestedUpdateId: updateId,
      });
      if (result.status === UserSuggestedUpdateStatus.failed) {
        throw new Error(result.reason);
      } else {
        message.success(
          userUpdateModalValues[updateType].handleUpdateSuccessMsg,
        );
        handleSetAuth(updateType, result);
      }
    } catch (err) {
      let msg = err.message;
      if (err.isAxiosError && err.response?.data?.message) {
        msg = err.response.data.message;
      }
      errorReporter.error(err);
      message.error(userUpdateModalValues[updateType].handleUpdateErrMsg(msg));
    } finally {
      setIsWorking(false);
      handleNextUpdateStep();
    }
  };

  useEffect(() => {
    if (suggestedUpdates) openModal();
  }, [suggestedUpdates, openModal]);

  if (!suggestedUpdates) return null;

  return (
    <Modal
      open={visible}
      onCancel={closeModal}
      cancelText="Close"
      title={'Update User Data'}
      footer={null}
      width={breakpoints.sm}
    >
      <StyledSteps size="small" current={currentUpdateStep} dataCy="">
        {suggestedUpdates.map(({ type }, idx) => (
          <Step
            icon={userUpdateModalValues[type].icon(currentUpdateStep > idx)}
            key={type}
            title={userUpdateModalValues[type].label}
            dataCy=""
          />
        ))}
      </StyledSteps>
      {suggestedUpdates.map(({ id, type, newValue, prevValue }, idx) =>
        idx === currentUpdateStep ? (
          <StyledVerticalSpace size="large" key={type}>
            <Title textAlign="center">
              {userUpdateModalValues[type].title}
            </Title>
            <Card>
              <VerticalSpace>
                <Text strong>New</Text>
                {userUpdateModalValues[type].updateValueText(newValue)}
                <SecondaryDescriptionText>
                  {userUpdateModalValues[type].description}
                </SecondaryDescriptionText>
              </VerticalSpace>
            </Card>

            <Card>
              <VerticalSpace>
                <Text strong>Previous</Text>
                {userUpdateModalValues[type].updateValueText(prevValue)}
              </VerticalSpace>
            </Card>
            <StyledModalFooter>
              <Button
                loading={isWorking}
                onClick={() => {
                  handleKeepPrevious(id, type);
                }}
              >
                Keep Previous
              </Button>
              <Button
                loading={isWorking}
                onClick={() => {
                  handleUpdate(id, type);
                }}
                type="primary"
              >
                {`Update ${userUpdateModalValues[type].label}`}
              </Button>
            </StyledModalFooter>
          </StyledVerticalSpace>
        ) : null,
      )}
    </Modal>
  );
};

const StyledVerticalSpace = styled(VerticalSpace)`
  text-align: center;
`;
const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: space-evenly;
`;

const StyledSteps = styled(Steps)`
  width: 60%;
  margin: auto;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;
